<script setup lang="ts">
import { watch, onUnmounted, ref, nextTick, Ref } from 'vue';
import router from '@/router';
import { useUserStore } from '@/stores/user';
import { Employee } from '@/lib/request/types/employee';
import { useEmployeeStore } from '@/stores/employees';
import { storeToRefs } from 'pinia';
import { getTranslations } from '@/lib/util/i18n';
import { CoButton } from '@deputy/copilot2';
import ProfilePicture from './ProfilePicture.vue';
import DeleteLeft from './icons/DeleteLeft.vue';

const props = defineProps<{ employeeId: number }>();

const employee = useEmployeeStore().getByEmployeeId(
  props.employeeId
) as Employee;

const emit = defineEmits(['login', 'addToPin']);

const t = getTranslations({
  app: '',
  module: 'views',
  file: 'pin-form',
});

// PIN
const pin = ref('');
const pinLength = 4;

function addToPin(key: string) {
  if (['Backspace', 'Delete'].includes(key)) {
    pin.value = pin.value.slice(0, -1);
  }
  if (pin.value.length < pinLength && key.match(/^[0-9]$/)) {
    pin.value += key;
  }
}

watch(pin, () => {
  if (pin.value.length === pinLength) {
    submit();
  }
});

function submit() {
  emit('login', pin.value);
}

// KEYPAD
const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', 'Delete'];

window.addEventListener('keydown', keyDown);

/* istanbul ignore next */
onUnmounted(() => {
  window.removeEventListener('keydown', keyDown);
});

function keyDown(e: KeyboardEvent) {
  addToPin(e.key);
  emit('addToPin', pin.value);
}

// ERRORS
const user = useUserStore();
const { errors } = storeToRefs(user);
const showError = ref(false);
const errorElement = ref<HTMLElement>();

watch(errors.value, assessError);
async function assessError() {
  if (errors.value.authError) {
    showError.value = true;
    pin.value = '';

    // need to wait for the error to be rendered before we can access it to add the shake class
    await nextTick();
    shake(errorElement);
  }
}

/* istanbul ignore next */
function shake(el: Ref<HTMLElement | undefined>) {
  el.value?.classList.add('shake');
  setTimeout(() => {
    errorElement.value?.classList.remove('shake');
  }, 1000);
}
</script>

<template>
  <form @submit.prevent>
    <div class="__user-data">
      <ProfilePicture :src="employee.Photo" size="large" />

      <h1>{{ employee.DisplayName }}</h1>
    </div>
    <div class="__form">
      <p class="__enter-pin" v-if="!showError">{{ t('enter-pin') }}</p>
      <p
        v-else
        ref="errorElement"
        class="__pin-error"
        data-test-id="error"
        aria-live="polite"
      >
        {{ user.errors.authError?.message }}
      </p>

      <div class="__pin-container">
        <div
          v-for="i in pinLength"
          :key="i"
          class="__pin-box"
          :class="{
            error: showError && pin.length === 0,
            '__pin-box--filled': pin.charAt(i - 1),
          }"
        />
      </div>

      <div class="__num-pad" data-dd-privacy="hidden">
        <CoButton
          v-for="num in keys"
          :key="num"
          :class="`__key-${num}`"
          @click="addToPin(num)"
        >
          <DeleteLeft
            v-if="num === 'Delete'"
            style="vertical-align: text-bottom"
          />
          <span v-else>
            {{ t(`num-pad.${num}`) }}
          </span>
        </CoButton>
      </div>

      <CoButton
        type="text"
        @click="router.push({ name: 'reset-pin', params: { id: employeeId } })"
        data-test-id="forgot-pin"
      >
        {{ t('forgot-pin') }}
      </CoButton>
    </div>
  </form>
</template>

<style scoped>
form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  text-align: center;
}
.__form,
.__user-data {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  text-align: center;
}

h1 {
  margin-bottom: 1rem;
}

.__enter-pin {
  font-size: 17px;
  line-height: 22px;
}

.__pin-error {
  font-size: 17px;
  line-height: 22px;
  color: var(--co-status-red);
}

.__pin-container {
  display: flex;
  gap: 32px;
  justify-content: center;
  margin: 0 0 16px;
}

.__pin-box {
  --size: 20px;
  width: var(--size);
  height: var(--size);
  border: 2px solid var(--co-lines-border-solid);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.__pin-box.error {
  border-color: var(--co-status-red);
}

.__pin-box--filled {
  background-color: var(--co-lines-border-solid);
}

.__num-pad {
  display: grid;
  grid-template-columns: repeat(3, 100px);
  gap: 1.3rem;
  margin-bottom: 1.5rem;
}

.__num-pad .co-button {
  height: 44px;
  font-size: 1.1rem;
  touch-action: manipulation;
}

.__key-0 {
  grid-column-start: 2;
}

form .co-button {
  margin: 0 !important;
}

.__close-container {
  align-self: end;
  margin-bottom: 0.75rem;
  /*
  we want to translate the close icon by its padding so it looks
  aligned with everything else, without loosing the larger touch area.
  We have to translate the parent wrapper so we don't override
  the buttons transform: scale property.
  */
  transform: translate(1rem, -0.5rem);
}

@media (max-height: 628px) {
  form {
    flex-direction: row;
    gap: 2rem;
    justify-content: center;
    align-items: stretch;
  }

  .__user-data {
    flex: 1;
    border-right: 2px solid var(--co-lines-border-solid);
    min-height: 100%;
    padding-right: 2rem;
    align-items: stretch;
    justify-content: center;
  }

  .__form {
    flex: 2;
  }

  h1 {
    font-size: 20px;
  }

  .co-dialog__body {
    top: -30px;
  }

  .__enter-pin {
    font-weight: 500;
    font-size: 22px;
  }
}
</style>
